import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { api } from '@/lib/api'
import { formatRelativeTime } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { ArrowDownRight, ArrowUpRight, FileText, Plus, Sparkles } from 'lucide-react'
import {
  Area,
  AreaChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

const metrics = [
  { key: 'totalForms', title: '總問卷數', suffix: '', trend: 12 },
  { key: 'monthlyResponses', title: '本月回應數', suffix: '', trend: 18 },
  { key: 'averageCompletionRate', title: '平均完成率', suffix: '%', trend: 7 },
  { key: 'averageTimeSeconds', title: '平均作答時間', suffix: ' 秒', trend: -4 },
] as const

export default function DashboardHomeRoute() {
  const navigate = useNavigate()
  const stats = useQuery({ queryKey: ['dashboard-stats'], queryFn: api.dashboardStats })

  if (stats.isLoading || !stats.data) {
    return (
      <div className="p-4 lg:p-6">
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 p-4 lg:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">儀表板</h1>
          <p className="mt-1 text-sm text-slate-500">追蹤問卷、回應與完成率的即時狀態。</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <Sparkles className="size-4" />
            瀏覽範本
          </Button>
          <Button onClick={() => void navigate({ to: '/dashboard/forms' })}>
            <Plus className="size-4" />
            建立問卷
          </Button>
        </div>
      </div>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric) => {
          const value = stats.data[metric.key]
          const positive = metric.trend >= 0
          return (
            <Card key={metric.key}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm text-slate-500">{metric.title}</CardTitle>
                  {positive ? (
                    <ArrowUpRight className="size-4 text-emerald-600" />
                  ) : (
                    <ArrowDownRight className="size-4 text-rose-600" />
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold">
                  {value}
                  {metric.suffix}
                </div>
                <p
                  className={
                    positive ? 'mt-1 text-sm text-emerald-600' : 'mt-1 text-sm text-rose-600'
                  }
                >
                  較上月 {positive ? '+' : ''}
                  {metric.trend}%
                </p>
                <div className="mt-4 h-10">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.data.responseTrend.slice(-10)}>
                      <Line
                        type="monotone"
                        dataKey="responses"
                        stroke="#6366F1"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </section>

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <div>
            <CardTitle>回應趨勢</CardTitle>
            <p className="mt-1 text-sm text-slate-500">最近 30 天每日回應數。</p>
          </div>
          <div className="flex rounded-md bg-slate-100 p-1 text-sm">
            {['7天', '30天', '90天'].map((item) => (
              <button
                key={item}
                type="button"
                className="rounded px-3 py-1 font-medium text-slate-600 first:bg-white first:text-slate-950 first:shadow-sm"
              >
                {item}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.data.responseTrend}>
                <defs>
                  <linearGradient id="responses" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="5%" stopColor="#6366F1" stopOpacity={0.28} />
                    <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
                <Tooltip />
                <Area dataKey="responses" stroke="#6366F1" fill="url(#responses)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <section className="grid gap-4 xl:grid-cols-[1.5fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>最活躍問卷</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>問卷名稱</TableHead>
                  <TableHead>回應數</TableHead>
                  <TableHead>完成率</TableHead>
                  <TableHead>最後回應</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.data.activeForms.map((form) => (
                  <TableRow key={form.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <FileText className="size-4 text-indigo-600" />
                        {form.title}
                      </div>
                    </TableCell>
                    <TableCell>{form.responses}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={form.completionRate} className="w-24" />
                        <span className="text-xs text-slate-500">{form.completionRate}%</span>
                      </div>
                    </TableCell>
                    <TableCell>{formatRelativeTime(form.updatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>最新回應</CardTitle>
          </CardHeader>
          <CardContent className="flex max-h-[400px] flex-col gap-3 overflow-y-auto">
            {stats.data.latestResponses.map((response) => (
              <div key={response.id} className="rounded-md border border-slate-200 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium">{response.formTitle}</div>
                  <Badge variant="secondary">{formatRelativeTime(response.submittedAt)}</Badge>
                </div>
                <p className="mt-2 line-clamp-2 text-sm text-slate-500">{response.preview}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>
    </div>
  )
}
